import type { AuthSession, SessionResponse } from "./types"

export async function readPasswordResetError(response: Response): Promise<string> {
  const fallback = "Could not send the password reset email."
  const text = await response.text()
  if (!text) return fallback

  try {
    const payload = JSON.parse(text) as {
      error?: string
      error_description?: string
      message?: string
    }
    return safePasswordResetError(payload.error_description ?? payload.message ?? payload.error, fallback)
  } catch {
    return safePasswordResetError(text, fallback)
  }
}

function safePasswordResetError(message: string | undefined, fallback: string) {
  const trimmed = message?.trim()
  if (!trimmed) return fallback
  if (
    /\b(auth0|bucket|constraint|database|db|endpoint|er_[a-z0-9_]+|mysql|s3|sql|typeorm)\b/i.test(trimmed)
  ) {
    return fallback
  }
  return /^Could not [a-z][\w\s-]+\.$/.test(trimmed) ? trimmed : fallback
}

export async function readServerSession(): Promise<AuthSession | null> {
  const response = await fetch("/api/dashboard/auth/session", {
    method: "GET",
    credentials: "same-origin",
  })
  if (!response.ok) return null
  const payload = (await response.json()) as SessionResponse
  if (!payload.authenticated) return null
  return sessionFromResponse(payload)
}

export async function refreshServerSession(): Promise<AuthSession | null> {
  const response = await fetch("/api/dashboard/auth/refresh", {
    method: "POST",
    credentials: "same-origin",
  })
  if (!response.ok) return null
  const payload = (await response.json()) as SessionResponse
  if (!payload.authenticated) return null
  return sessionFromResponse(payload)
}

function sessionFromResponse(payload: SessionResponse): AuthSession {
  return {
    user: payload.user,
    expiresAt: payload.expiresAt,
    idleExpiresAt: payload.idleExpiresAt,
    absoluteExpiresAt: payload.absoluteExpiresAt,
  }
}
